import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  HeartIcon, 
  PhoneIcon, 
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { useSettings } from '../context/SettingsContext';
import { settingsAPI } from '../services/api';
import SocialIcon from './SocialIcon';
import ZarinpalBadge from './ZarinpalBadge';

const DesktopFooter = () => {
  const { systemSettings, resolveMediaUrl } = useSettings();
  const [premiumServices, setPremiumServices] = useState([]);

  useEffect(() => {
    const fetchPremiumServices = async () => {
      try {
        const response = await settingsAPI.getPremiumServices();
        const data = response.data;
        if (Array.isArray(data)) {
          setPremiumServices(data);
        } else if (data && Array.isArray(data.results)) {
          setPremiumServices(data.results);
        } else {
          setPremiumServices([]);
        }
      } catch (error) {
        console.error('Error fetching premium services:', error);
      }
    };
    fetchPremiumServices();
  }, []);

  return (
    <footer className="hidden lg:block bg-gradient-to-br from-gray-900 via-kadolar-900 to-gray-900 text-white border-t border-white/10 relative overflow-hidden font-sans">
      {/* Decorative Glow Effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-600/10 rounded-full blur-[128px] pointer-events-none"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-600/5 rounded-full blur-[128px] pointer-events-none"></div>

      {/* Top Section: Features (Luxury Glass Strip) */}
      <div className="border-b border-white/10 bg-black/20 backdrop-blur-sm relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-4 gap-8 divide-x divide-x-reverse divide-white/5">
            {premiumServices.length > 0 ? (
              premiumServices.slice(0, 4).map((service) => (
                <FeatureItem 
                  key={service.id}
                  iconUrl={service.icon_url} 
                  title={service.title} 
                  desc={service.text} 
                />
              ))
            ) : (
              // Fallback if no services are returned from API
              <>
                <FeatureItem 
                  title="ارسال اکسپرس" 
                  desc="تحویل فوری با بسته‌بندی فاخر" 
                />
                <FeatureItem 
                  title="ضمانت اصالت" 
                  desc="تضمین اصالت تمام کالاها" 
                />
                <FeatureItem 
                  title="تضمین رضایت" 
                  desc="۷ روز ضمانت بازگشت وجه" 
                />
                <FeatureItem 
                  title="پشتیبانی VIP" 
                  desc="پاسخگویی اختصاصی ۲۴/۷" 
                />
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10">
          
          {/* Column 1: Brand & About (Width: 4/12) */}
          <div className="lg:col-span-4 space-y-5">
            <div className="flex items-center gap-4">
              {systemSettings?.logo_url ? (
                <div className="w-16 h-16 bg-gradient-to-br from-white/10 to-white/5 rounded-2xl flex items-center justify-center shadow-inner border border-white/10 backdrop-blur-md overflow-hidden">
                   <img 
                    src={resolveMediaUrl(systemSettings.logo_url)} 
                    alt={systemSettings?.site_name || 'کادولار'} 
                    className="w-full h-full object-contain p-2"
                  />
                </div>
              ) : (
                <div className="w-16 h-16 bg-gradient-to-br from-white/10 to-white/5 rounded-2xl flex items-center justify-center shadow-inner border border-white/10 backdrop-blur-md">
                   <span className="text-2xl font-bold text-amber-400">K</span>
                </div>
              )}
              <div>
                <h3 className="text-2xl font-bold text-white tracking-tight">
                  {systemSettings?.site_name || 'کادولار'}
                </h3>
                <p className="text-amber-500/90 text-xs mt-1.5 font-medium tracking-[0.2em] uppercase">Luxury Gift Shop</p>
              </div>
            </div>
            
            <p className="text-gray-300 leading-8 text-justify font-light text-[15px] opacity-90 border-l-2 border-amber-500/30 pl-4">
              کادولار فراتر از یک فروشگاه است؛ روایتی است از شکوه و زیبایی. ما مجموعه‌ای از نفیس‌ترین هدایا را گرد آورده‌ایم تا لحظات شما را جاودانه کنیم. هر محصول، با دقت و وسواس انتخاب شده تا شایسته سلیقه شما باشد.
            </p>
          </div>

          {/* Column 2: Quick Links (Width: 2/12) */}
          <div className="lg:col-span-2 lg:col-start-6 lg:mt-14">
            <FooterHeader title="دسترسی سریع" />
            <ul className="space-y-2">
              <FooterLink to="/" text="صفحه اصلی" />
              <FooterLink to="/categories" text="دسته‌بندی‌ها" />
              <FooterLink to="/about" text="درباره ما" />
              <FooterLink to="/contact" text="تماس با ما" />
            </ul>
          </div>

          {/* Column 3: Contact Info (Width: 3/12) */}
          <div className="lg:col-span-3 lg:mt-14">
            <FooterHeader title="ارتباط با ما" />
            <ul className="space-y-3">
              {systemSettings?.contact_phone && (
                <li>
                  <a href={`tel:${systemSettings.contact_phone}`} className="group flex items-center gap-4 text-gray-300 hover:text-white transition-colors">
                    <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center border border-white/5 group-hover:border-amber-500/50 group-hover:text-amber-400 transition-all duration-300">
                      <PhoneIcon className="h-5 w-5" />
                    </div>
                    <span dir="ltr" className="text-lg font-light tracking-wider">{systemSettings.contact_phone}</span>
                  </a>
                </li>
              )}
              {systemSettings?.contact_email && (
                <li>
                  <a href={`mailto:${systemSettings.contact_email}`} className="group flex items-center gap-4 text-gray-300 hover:text-white transition-colors">
                    <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center border border-white/5 group-hover:border-amber-500/50 group-hover:text-amber-400 transition-all duration-300">
                      <EnvelopeIcon className="h-5 w-5" />
                    </div>
                    <span className="text-sm font-light">{systemSettings.contact_email}</span>
                  </a>
                </li>
              )}
              
            </ul>
          </div>

          {/* Column 4: Trust & Social (Width: 2/12) */}
          <div className="lg:col-span-2 ">
             <FooterHeader title="همراه ما باشید" />
             <div className="flex flex-wrap gap-2 mb-5">
              <SocialIcon type="instagram" url={systemSettings?.contact_instagram_url} />
              <SocialIcon type="telegram" url={systemSettings?.contact_telegram_url} />
              <SocialIcon type="whatsapp" url={systemSettings?.contact_whatsapp_url} />
            </div>

            <div className="border-t border-white/10 pt-4">
              <h6 className="text-xs font-bold text-gray-400 mb-3 uppercase tracking-wider">نمادهای اطمینان</h6>
              <div className="grid grid-cols-2 gap-2">
                  <div className="aspect-square bg-white/90 rounded-xl flex items-center justify-center hover:scale-105 transition-all duration-300 cursor-pointer overflow-hidden p-2 shadow-lg shadow-black/20">
                     <ZarinpalBadge />
                  </div>
                 <div className="aspect-square bg-white/90 rounded-xl flex items-center justify-center hover:scale-105 transition-all duration-300 cursor-pointer overflow-hidden p-2 shadow-lg shadow-black/20">
                    <span className="text-[10px] text-gray-800 text-center font-bold border-2 border-gray-800 rounded px-1 py-0.5">ENAMAD</span>
                 </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-gray-400 text-xs font-light tracking-wide">
              © {new Date().getFullYear()} تمامی حقوق برای فروشگاه <span className="text-amber-500 font-medium">{systemSettings?.site_name || 'کادولار'}</span> محفوظ است.
            </div>
            
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-gray-500 hover:text-amber-400 text-xs transition-colors duration-300">
                حریم خصوصی
              </Link>
              <span className="text-gray-700">|</span>
              <Link to="/terms" className="text-gray-500 hover:text-amber-400 text-xs transition-colors duration-300">
                قوانین و مقررات
              </Link>
              <span className="text-gray-700">|</span>
              <div className="flex items-center gap-1 text-gray-500 text-xs">
                <span>Design by</span>
                <HeartIcon className="w-3 h-3 text-red-500 fill-red-500" />
                <span className="text-gray-400">Kadolar Team</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Helper Components
const FeatureItem = ({ iconUrl, icon: Icon, title, desc }) => (
  <div className="flex flex-col items-center text-center group p-2">
    <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-3 group-hover:bg-amber-500 group-hover:text-white text-amber-500 transition-all duration-500 border border-white/10 group-hover:scale-110 group-hover:shadow-[0_0_15px_rgba(245,158,11,0.5)] overflow-hidden">
      {iconUrl ? (
        <img src={iconUrl} alt={title} className="w-6 h-6 object-contain transition-all duration-500 brightness-0 invert group-hover:brightness-100 group-hover:invert-0" />
      ) : Icon ? (
        <Icon className="w-6 h-6 transition-colors duration-500" />
      ) : null}
    </div>
    <h5 className="font-bold text-white text-sm mb-1 group-hover:text-amber-400 transition-colors duration-300">{title}</h5>
    <p className="text-gray-400 text-xs font-light group-hover:text-gray-300">{desc}</p>
  </div>
);

const FooterHeader = ({ title }) => (
  <h4 className="text-base font-bold mb-4 text-white flex items-center gap-2 border-r-2 border-amber-500 pr-3">
    {title}
  </h4>
);

const FooterLink = ({ to, text }) => (
  <li>
    <Link to={to} className="text-gray-400 hover:text-amber-400 hover:pr-2 transition-all duration-300 flex items-center text-sm font-light group">
      <span className="w-1 h-1 bg-gray-600 rounded-full ml-2 group-hover:bg-amber-500 transition-colors duration-300"></span>
      {text}
    </Link>
  </li>
);

export default DesktopFooter;
